import userManager, { trackTokenExpiration } from 'lib/auth/UserManager';
import { User } from 'oidc-client';
import { userFound } from 'redux-oidc';
import store from 'store/Store';
import { isExpired } from './decodeAccessToken';
import { useHistory } from 'react-router';

export const handleAzureAdB2cCallback = async (
	onSuccess: (user?: User) => void,
	onError: (error: Error | null, status: string) => void
) => {
	await userManager.clearStaleState();
	const hash = window.location.hash;
	const hashAttributes = hash.split('&').reduce((acc, value) => {
		const parts: string[] = value.split('=');
		acc[parts[0]] = parts[1];
		return acc;
	}, {} as any);

	/*
  Handle forgot password error sent by AzureAD B2C
  AADB2C90118 is the error code to be handled when forgot password link is clicked on AzureAd B2C
  Ref: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview#linking-user-flows
  */
	if (hash && hash.includes('AADB2C90091')) {
		// this error code occurs when the user its cancel during a password reset flow
		window.location.replace('/');
	} else if (window.location.pathname.startsWith('/auth-callback/') && hash && hash.includes('error')) {
		// this doesn't seem to be doing its intended job, the user is left with a blank screen it seems.
		// to be investigated
		return Promise.reject(new Error(decodeURI(hashAttributes.error_description))).catch((e: Error) =>
			onError(e, 'AZURE_B2C_CALLBACK_ERROR')
		);
	} else if (window.location.pathname === '/silent-token-renewal-callback/') {
		handleSilentTokenRenewal();
	} else if (window.location.pathname.startsWith('/auth-callback/')) {
		signinRedirectCallback()
			.then(onSuccess)
			.catch((e: Error) => onError(e, 'AZURE_REDIRECT_CALLBACK_ERROR'));
	} else {
		ensureUserLoggedIn()
			.then(onSuccess)
			.catch((e: Error) => onError(e, 'ENSURE_USER_LOGGED_IN_ERROR'));
	}
};

const handleSilentTokenRenewal = async () => {
	try {
		// This request is handled inside the oidc iFrame
		await userManager.signinSilentCallback();
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};

const signinRedirectCallback = async () => {
	try {
		const user = await userManager.signinRedirectCallback();
		if (user) {
			//Need to manually dispatch the userFound action because at this point the app hasn't loaded yet
			store.dispatch(userFound(user));
			window.history.pushState(null, document.title, '/');
		}
		return user;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};

const ensureUserLoggedIn = async () => {
	try {
		const user = await userManager.getUser();

		//user.expired check for the access_token expiry and not the id_token. a custom check needed
		if (user && !isExpired(user.id_token)) {
			trackTokenExpiration(user.id_token);
			return user;
		} else {
			await userManager.signinRedirect();
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}

	throw new Error('Unable to log in user');
};
